/**
 * "Fixed parts and part bundles" on a service (#3845, epic #3841).
 *
 * Edits drafts only; ServiceFormDialog saves them with the rest of the service. A bundle is
 * shown as one row and expands into part lines only when the service is copied (decision 3).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { PartCatalogInput } from '@/components/workorders/PartCatalogInput';
import type { PartBundleExpansion, PartListItem } from '@/hooks/useInventory';
import { formatCentsForDisplay } from '@/utils/money';
import { ServiceBundlePicker } from './ServiceBundlePicker';
import type { BundleDraft, FixedPartDraft } from './serviceFixedParts';

export interface ServiceFixedPartsSectionProps {
  parts: FixedPartDraft[];
  bundles: BundleDraft[];
  /** Part category id → name of the categories required on this service. */
  requiredCategoryNames: ReadonlyMap<string, string>;
  onAddPart: (part: PartListItem) => void;
  onRemovePart: (id: string) => void;
  onPartQuantityInput: (id: string, input: string) => void;
  onPartQuantityBlur: (id: string) => void;
  onAddBundle: (expansion: PartBundleExpansion) => void;
  onRemoveBundle: (id: string) => void;
}

export function ServiceFixedPartsSection({
  parts,
  bundles,
  requiredCategoryNames,
  onAddPart,
  onRemovePart,
  onPartQuantityInput,
  onPartQuantityBlur,
  onAddBundle,
  onRemoveBundle,
}: ServiceFixedPartsSectionProps) {
  const { t } = useTranslation('settings');
  const [search, setSearch] = useState('');

  return (
    <Box data-testid="service-fixed-parts">
      <Typography variant="subtitle2" gutterBottom>
        {t('servicesTab.parts.fixedTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('servicesTab.parts.fixedHelp')}
      </Typography>

      {parts.length === 0 && bundles.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t('servicesTab.parts.noFixedParts')}
        </Typography>
      )}

      {parts.length > 0 && (
        <Table size="small" sx={{ mb: 1 }} aria-label={t('servicesTab.parts.fixedPartsTable')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('servicesTab.parts.partName')}</TableCell>
              <TableCell sx={{ width: 130 }}>{t('servicesTab.parts.partSku')}</TableCell>
              <TableCell sx={{ width: 100 }}>{t('servicesTab.parts.quantity')}</TableCell>
              <TableCell sx={{ width: 120 }} align="right">
                <Tooltip title={t('servicesTab.parts.catalogPriceHint')}>
                  <span>{t('servicesTab.parts.catalogPrice')}</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {parts.map((part) => {
              const sameCategory = part.categoryId != null ? requiredCategoryNames.get(part.categoryId) : undefined;
              return (
                <TableRow key={part.id}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <span>{part.name}</span>
                      {!part.isActive && (
                        <Tooltip title={t('servicesTab.parts.inactiveHint')}>
                          <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            icon={<WarningAmberIcon />}
                            label={t('servicesTab.parts.inactive')}
                          />
                        </Tooltip>
                      )}
                    </Stack>
                    {sameCategory !== undefined && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('servicesTab.parts.sameCategoryHint', { category: sameCategory })}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{part.sku}</TableCell>
                  <TableCell>
                    <TextField
                      value={part.quantityInput}
                      onChange={(e) => onPartQuantityInput(part.id, e.target.value)}
                      onBlur={() => onPartQuantityBlur(part.id)}
                      size="small"
                      variant="outlined"
                      sx={{ width: 90 }}
                      slotProps={{
                        htmlInput: {
                          inputMode: 'decimal',
                          'aria-label': t('servicesTab.parts.quantityFor', { name: part.name }),
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {formatCentsForDisplay(part.catalogUnitPriceCents)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={t('servicesTab.parts.removePart')}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => onRemovePart(part.id)}
                        aria-label={t('servicesTab.parts.removePart')}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {bundles.length > 0 && (
        <Table size="small" sx={{ mb: 1 }} aria-label={t('servicesTab.parts.bundlesTable')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('servicesTab.parts.bundleName')}</TableCell>
              <TableCell sx={{ width: 160 }}>{t('servicesTab.parts.bundleItems')}</TableCell>
              <TableCell sx={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {bundles.map((bundle) => (
              <TableRow key={bundle.id}>
                <TableCell>{bundle.name}</TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {t('servicesTab.parts.bundleItemCount', { count: bundle.itemCount })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title={t('servicesTab.parts.removeBundle')}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onRemoveBundle(bundle.id)}
                      aria-label={t('servicesTab.parts.removeBundle')}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'flex-start' }}>
        <Box sx={{ flex: 1, maxWidth: { sm: 400 }, display: 'flex' }}>
          <PartCatalogInput
            value={search}
            onChange={setSearch}
            onSelect={(part) => {
              onAddPart(part);
              setSearch('');
            }}
            placeholder={t('servicesTab.parts.searchPart')}
            helperText={t('servicesTab.parts.searchPartHint')}
          />
        </Box>
        <Box>
          <ServiceBundlePicker onInsert={onAddBundle} />
        </Box>
      </Stack>
    </Box>
  );
}
